import { useState, useEffect } from 'react';
import { Button, Input, Select, Icon, Header, Modal, DatePicker, Popup, VerticalMenu } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { useTranslation } from 'react-i18next';
import { RoleComponent } from 'components/roleComponent';
import useFetch from 'helpers/hooks/useFetch';
import { COMPANY_MANAGER, BSLBR, notificationAtom } from 'resources';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { ContractDTO, SelectOption, FilterContractDTO } from 'models/bslbr/general';
import { CompanyFilterDTO } from 'models';
import AddContractByForm from './new-contract/addContractByForm';
import AddWarrantyByForm from './new-warranty/addWarrantyByForm';
import ContractDetails from './contractDetails';
import ContractReport from './contractReport';
import { formatDate } from 'helpers';
import { downloadFileExcelContract } from 'resources/api/bslbr_api/fileReports';
import { bslbrCountriesAtom, bslbrPartnersAtom, bslbrPartsAtom } from 'resources/atoms/bslbrAtom';
import { CompanyDTO } from 'models/userManager/addCustomer';
import useUser from '../../../helpers/hooks/useUser';

const InitialFilter: FilterContractDTO = {
    Partner: [],
    Country: [],
    PartNumber: [],
    PageIndex: 1,
    PageSize: 10,
    Cnpj: '',
    ContractNumber: '',
    WarrantyNumber: '',
    Status: '',
    SerialNumber: '',
    DateStart: '',
    DateEnd: '',
    SaleDate: '',
    CustomerName: '',
    Type: '',
    SLAPriority: ''
};

const InitialContract: ContractDTO = {
    Id: 0,
    Number: '',
    ContractNumber: '',
    WarrantyNumber: '',
    Status: '',
    Partner: '',
    Cnpj: '',
    CustomerName: '',
    CustomerAddress: '',
    CustomerCity: '',
    CustomerCountry: '',
    CustomerContactName: '',
    CustomerEmail: '',
    CustomerPhone: '',
    EquipmentLocationName: '',
    EquipmentLocationAddress: '',
    EquipmentLocationCity: '',
    EquipmentLocationCountry: '',
    SaleDate: '',
    StartDate: '',
    EndDate: '',
    InactivateDate: '',
    ContractValue: '',
    HourlyContractValue: '',
    WarrantyHourlyValue: '',
    CustomerNumber: '',
    TrackingNumber: '',
    PurchaseOrder: '',
    SiteCount: 0,
    Description: '',
    Comments: '',
    Type: '',
    Sla: '',
    Quantity: 0,
    SoldTo: '',
    ItemNumber: '',
    Equipments: []
};

function ContractSearch() {
    const setNotification = useSetRecoilState(notificationAtom);
    const [partners, setPartners] = useRecoilState(bslbrPartnersAtom);
    const [countries, setCountries] = useRecoilState(bslbrCountriesAtom);
    const [parts, setParts] = useRecoilState(bslbrPartsAtom);
    const [partnerNameFilter, setPartnerNameFilter] = useState<string>("");
    const { t } = useTranslation(['common', 'bslbr', 'user', 'home', 'notification']);

    const [openModalInactivateDelete, setOpenModalInactivateDelete] = useState(false);
    const [openModalActivate, setOpenModalActivate] = useState(false);
    const [idContract, setIdContract] = useState<number | null>(null);
    const [contractDetails, setContractDetails] = useState<ContractDTO>(InitialContract);
    const [filteredData, setFilteredData] = useState<ContractDTO[]>([]);
    const [filters, setFilters] = useState(InitialFilter);
    const [showAddContract, setShowAddContract] = useState(false);
    const [showAddWarranty, setShowAddWarranty] = useState(false);
    const [showContractDetails, setShowContractDetails] = useState(false);
    const [showContractReport, setShowContractReport] = useState(false);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [allPartnerOptions, setAllPartnerOptions] = useState<[]>([]);
    const [partsOptions, setPartsOptions] = useState<SelectOption[]>([]);
    const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
    const { user: userDetails } = useUser();
    const isClient = userDetails?.role === '3' || userDetails?.role === '4';

    function handleSuccess() {
        setNotification({ type: 'success', message: 'Report' });
    }
    const handleError = () => {
        setNotification({ type: 'information', message: 'NoData' });
    };

    const contractType: SelectOption[] = [
        { value: 'Contract', text: t('bslbr:translation.Contract') },
        { value: 'Warranty', text: t('bslbr:translation.Warranty') }
    ];

    const statusType: SelectOption[] = [
        { value: 'Active', text: t('bslbr:translation.Active') },
        { value: 'Inactive', text: t('bslbr:translation.Inactive') }
    ];

    const SLAPriority = [
        { value: '1', text: '1' },
        { value: '2', text: '2' },
        { value: '5', text: '5' },
        { value: '15', text: '15' }
    ];

    const [inactivate, inactivateContract, loadingInactivate] = useFetch<{ contractId: number | null }>({
        url: BSLBR.inactivateContract,
        method: 'GET',
        onError: 'LoadInfo'
    });

    const [activated, activateContract, loadingActivate] = useFetch<{ contractId: number | null }>({
        url: BSLBR.activateContract,
        method: 'GET',
        onError: 'LoadInfo'
    });

    const [, getContracts, loadingContracts] = useFetch<FilterContractDTO, ContractDTO[]>({
        url: BSLBR.getContracts,
        method: 'POST',
        onError: 'LoadInfo'
    });

    const [partner, getAllPartners] = useFetch<any, CompanyFilterDTO>({
        url: COMPANY_MANAGER.getCompanies,
        method: 'POST',
        onError: 'LoadInfo'
    });

    const [, getParts] = useFetch({
        url: BSLBR.getParts,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, getPartner] = useFetch({
        url: `${COMPANY_MANAGER.getBslBrasilPartners}?filter=${partnerNameFilter}`,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    useEffect(() => {
        getFilterContracts(filters);
        getAllPartnersByIdSap();
        if (!partners.length) {
            getPartners();
        }
        if (!parts.length) {
            getAllParts();
        }
    }, []);

    useEffect(() => {
        getFilterContracts(filters);
    }, [inactivate]);

    useEffect(() => {
        getFilterContracts(filters);
    }, [activated]);

    useEffect(() => {
        const partsOptions = parts.map(part => ({
            value: part.PartNumber,
            text: part.PartNumber
        }));
        setPartsOptions(partsOptions);
    }, [parts]);

    const getFilterContracts = async (filters: FilterContractDTO) => {
        try {
            await getContracts({ ...filters }, (current: any) => {
                if (current.data.Data) {
                    setFilteredData(
                        current.data.Data.map((item: any) => ({
                            ...item,
                            Number: item.ContractNumber || item.WarrantyNumber
                        }))
                    );
                    setTotalItems(current.data.TotalCount);
                } else {
                    setNotification(old => ({ ...old, type: 'information', message: t('bslbr:translation.NotFoundContracts') }));
                }
            });
        } catch (error) {
            setNotification(old => ({ ...old, type: 'critical', message: t('common:errors.Unexpected') }));
        }
    };

    const inactivateContracts = async (idContract: number | null) => {
        try {
            await inactivateContract({ contractId: idContract }, (current: any) => {
                if (!current.error) {
                    setNotification(old => ({ ...old, type: 'success', message: t('notification:success.Inactivate') }));
                }
            });
        } catch (error) {
            setNotification(old => ({ ...old, type: 'critical', message: t('common:errors.Unexpected') }));
        }
    };

    const activateContracts = async (idContract: number | null) => {
        try {
            await activateContract({ contractId: idContract }, (current: any) => {
                if (!current.error) {
                    setNotification(old => ({ ...old, type: 'success', message: t('notification:success.Activated') }));
                }
            });
        } catch (error) {
            setNotification(old => ({ ...old, type: 'critical', message: t('common:errors.Unexpected') }));
        }
    };

    const getAllPartnersByIdSap = async () => {
        try {
            await getAllPartners({ ...partner?.data }, (current: any) => {
                if (current?.data) {
                    setAllPartnerOptions(current.data.companies);
                } else {
                    setNotification(old => ({ ...old, type: 'information', message: t('bslbr:translation.NotFoundParts') }));
                }
            });
        } catch (error) {
            setNotification(old => ({ ...old, type: 'critical', message: t('common:errors.Unexpected') }));
        }
    };

    const handleExportExcel = async () => {
        setLoadingDownload(true);
        await downloadFileExcelContract(filters, `contracts.xlsx`, handleSuccess, handleError).finally(() =>
            setLoadingDownload(false)
        );
    };

    const getPartners = () => {
        getPartner(null, resp => {
            if (resp.data) {
                const partnersData = resp.data.map((partner: CompanyDTO) => ({
                    value: partner.name,
                    text: partner.name,
                    key: partner.id
                }));
                setPartners(partnersData);

                const uniqueCountries = Array.from(new Set(resp.data.map((partner: CompanyDTO) => String(partner.country))));
                const countriesData = uniqueCountries.map(country => ({
                    value: country as string,
                    text: country as string
                }));
                setCountries(countriesData);
            }
        });
    };

    const getAllParts = () => {
        getParts(null, (current: any) => {
            if (current.data.length) {
                setParts(current.data);
            } else {
                setNotification(old => ({ ...old, type: 'information', message: t('bslbr:translation.NotFoundParts') }));
            }
        });
    };

    const applyFilters = () => {
        getFilterContracts(filters);
    };

    const resetFilters = () => {
        setFilters(InitialFilter);
        getFilterContracts(InitialFilter);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            getPartners();
        }, 500);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerNameFilter]);

    const searchFieldsRender = () => {
        return (
            <div>
                <Header title={t('bslbr:translation.Contracts')} logo={false} menu={false}>
                    <div style={{ margin: '10px' }}>
                        <RoleComponent roles={['SUPERADMIN']}>
                            <Button
                                content={t('bslbr:translation.AddContracts')}
                                type="primary"
                                size="medium"
                                iconPosition="right"
                                icon={<Icon name="add" root="building" />}
                                onClick={() => setShowAddContract(true)}
                                description={t('bslbr:translation.AddContracts')}
                            />
                            <Button
                                content={t('bslbr:translation.AddWarranty')}
                                type="primary"
                                size="medium"
                                iconPosition="right"
                                icon={<Icon name="add" root="building" />}
                                onClick={() => setShowAddWarranty(true)}
                                description={t('bslbr:translation.AddWarranty')}
                            />
                        </RoleComponent>
                    </div>
                </Header>
                <div className="form4Columns">
                    <Select
                        value={filters.Type}
                        placeholder={t('common:translation.Type')}
                        label={t('common:translation.Type')}
                        options={contractType}
                        onChange={value => setFilters({ ...filters, Type: value })}
                    />
                    <Select
                        value={filters.Status}
                        placeholder={t('bslbr:translation.Status')}
                        label={t('bslbr:translation.Status')}
                        options={statusType}
                        onChange={value => setFilters({ ...filters, Status: value })}
                    />
                    <Select
                        value={filters.SLAPriority}
                        placeholder={t('bslbr:translation.SLA')}
                        label={t('bslbr:translation.SLA')}
                        options={SLAPriority}
                        onChange={value => setFilters({ ...filters, SLAPriority: value })}
                    />
                    <Input
                        placeholder={t('bslbr:translation.Warranty')}
                        label={t('bslbr:translation.Warranty')}
                        value={filters.WarrantyNumber}
                        onChange={value => setFilters({ ...filters, WarrantyNumber: value })}
                    />
                    <Input
                        placeholder={t('bslbr:translation.Contract')}
                        label={t('bslbr:translation.Contract')}
                        value={filters.ContractNumber}
                        onChange={value => setFilters({ ...filters, ContractNumber: value })}
                    />
                    <Select
                        placeholder={t('common:translation.PartNumber')}
                        label={t('common:translation.PartNumber')}
                        value={filters.PartNumber}
                        options={partsOptions}
                        search={true}
                        multiple={true}
                        onChange={value => setFilters({ ...filters, PartNumber: value })}
                    />
                    <Input
                        placeholder={t('bslbr:translation.Customer')}
                        label={t('bslbr:translation.Customer')}
                        value={filters.CustomerName}
                        onChange={value => setFilters({ ...filters, CustomerName: value })}
                    />
                    <Input
                        placeholder={t('common:translation.SerialNumber')}
                        label={t('common:translation.SerialNumber')}
                        value={filters.SerialNumber}
                        onChange={value => setFilters({ ...filters, SerialNumber: value })}
                    />
                    <DatePicker
                        label={t('bslbr:translation.StartDate')}
                        reserveSpace={false}
                        value={filters.DateStart}
                        onChange={value => setFilters({ ...filters, DateStart: value ? value.toISOString() : '' })}
                    />
                    <DatePicker
                        label={t('bslbr:translation.SaleDate')}
                        reserveSpace={false}
                        value={filters.SaleDate}
                        onChange={value => setFilters({ ...filters, SaleDate: value ? value.toISOString() : '' })}
                    />
                    <DatePicker
                        label={t('bslbr:translation.DuoDate')}
                        reserveSpace={false}
                        value={filters.DateEnd}
                        onChange={value => setFilters({ ...filters, DateEnd: value ? value.toISOString() : '' })}
                    />
                    <RoleComponent roles={['SUPERADMIN', 'ADMIN']}>
                        <Select
                            value={filters.Partner ?? []}
                            label={t('common:translation.Partner')}
                            placeholder={t('common:translation.Partner')}
                            options={partners}
                            multiple={true}
                            search={true}
                            onChange={value =>
                                setFilters(current => ({
                                    ...current,
                                    Partner: Array.isArray(value) ? value : []
                                }))
                            }
                            onSearch={value => setPartnerNameFilter(value)}
                        />
                    </RoleComponent>
                    <RoleComponent roles={['SUPERADMIN', 'ADMIN']}>
                        <Select
                            value={filters.Country ?? []}
                            placeholder={t('user:address.Country')}
                            label={t('user:address.Country')}
                            options={countries}
                            multiple={true}
                            search={true}
                            onChange={value =>
                                setFilters(current => ({
                                    ...current,
                                    Country: Array.isArray(value) ? value : []
                                }))
                            }
                        />
                    </RoleComponent>
                </div>

                <div className="buttonsSearch">
                    <Button
                        type="secondary"
                        icon="badge-delete"
                        content={t('common:actions.ResetFilters')}
                        iconPosition="right"
                        onClick={resetFilters}
                    />
                    <Button
                        type="primary"
                        icon="filter1"
                        content={t('common:actions.ApplyFilters')}
                        iconPosition="right"
                        onClick={applyFilters}
                    />
                </div>
            </div>
        );
    };

    const onInactivate = (idContract: number | null) => {
        setOpenModalInactivateDelete(false);
        inactivateContracts(idContract);
    };

    const onActivate = (idContract: number | null) => {
        setOpenModalActivate(false);
        activateContracts(idContract);
    };

    const renderModalInactivateContract = () => {
        return (
            <Modal
                open={openModalInactivateDelete}
                closeIcon={true}
                onClose={() => setOpenModalInactivateDelete(false)}
                closeOnDimmerClick={false}
                size="small"
                className="modal-excluir-content"
            >
                <Modal.Header>
                    <h1>{t('bslbr:translation.Inactivate')}</h1>
                </Modal.Header>
                <Modal.Content>
                    <p>{t('bslbr:translation.ConfirmInactivate')}</p>
                </Modal.Content>

                <Modal.Footer>
                    <Button
                        type="primary"
                        content={t('common:translation.Confirm')}
                        onClick={() => onInactivate(idContract)}
                        loading={loadingInactivate}
                    />
                </Modal.Footer>
            </Modal>
        );
    };

    const renderModalActivateContract = () => {
        return (
            <Modal
                open={openModalActivate}
                closeIcon={true}
                onClose={() => setOpenModalActivate(false)}
                closeOnDimmerClick={false}
                size="small"
                className="modal-excluir-content"
            >
                <Modal.Header>
                    <h1>{t('bslbr:translation.Activate')}</h1>
                </Modal.Header>
                <Modal.Content>
                    <p>{t('bslbr:translation.ConfirmActivate')}</p>
                </Modal.Content>

                <Modal.Footer>
                    <Button
                        type="primary"
                        content={t('common:translation.Confirm')}
                        onClick={() => onActivate(idContract)}
                        loading={loadingActivate}
                    />
                </Modal.Footer>
            </Modal>
        );
    };

    const tableContractRender = () => {
        return (
            <div className="Table-contract">
                <DataTable columnResizeMode="fit" data={filteredData} loading={loadingContracts}>
                    <DataTable.HeaderBar>
                        <DataTable.HeaderBar.Item
                            content={t('bslbr:translation.ExportExcel')}
                            iconSize="small"
                            icon="file-download"
                            onClick={handleExportExcel}
                        />
                    </DataTable.HeaderBar>
                    <DataTable.Column field={'Number'} align="center" header={t('Id')} sortable={true} />
                    <DataTable.Column
                        field="Type"
                        align="center"
                        header={t('common:translation.Type')}
                        sortable={true}
                        renderer={rowData => (
                            <>
                                {rowData.value === 'Contract' ? t('bslbr:translation.Contract') : t('bslbr:translation.Warranty')}
                            </>
                        )}
                    />
                    <DataTable.Column
                        field="Status"
                        align="center"
                        header={t('Status')}
                        sortable={true}
                        renderer={rowData => (
                            <>{rowData.value === 'Active' ? t('bslbr:translation.Active') : t('bslbr:translation.Inactive')}</>
                        )}
                    />
                    <DataTable.Column field="Partner" align="center" header={t('common:translation.Partner')} sortable={true} />
                    <DataTable.Column
                        field="CustomerName"
                        align="center"
                        header={t('bslbr:translation.Customer')}
                        sortable={true}
                    />
                    <DataTable.Column
                        field="StartDate"
                        align="center"
                        header={t('bslbr:translation.StartDate')}
                        sortable={true}
                        renderer={e => <>{formatDate(e?.rowData.StartDate)}</>}
                    />
                    <DataTable.Column
                        field="SaleDate"
                        align="center"
                        header={t('bslbr:translation.SaleDate')}
                        sortable={true}
                        renderer={e => <>{formatDate(e?.rowData.SaleDate)}</>}
                    />
                    <DataTable.Column
                        field="EndDate"
                        align="center"
                        header={t('bslbr:translation.EndDate')}
                        sortable={true}
                        renderer={e => <>{formatDate(e?.rowData.EndDate)}</>}
                    />
                    <DataTable.Column
                        field="opcoes"
                        disabled={isClient}
                        renderer={e => (
                            <Popup
                                className="popup-theme-wrap"
                                element={<Icon size="large" root="common" name="ellipsis-vertical" />}
                                on="click"
                                align="center"
                                disabled={isClient}
                            >
                                <VerticalMenu>
                                    <VerticalMenu.Header>{t('common:tabs.Options')}</VerticalMenu.Header>
                                    <VerticalMenu.Item
                                        onClick={() => {
                                            setShowContractDetails(true);
                                            setContractDetails(e.rowData);
                                        }}
                                    >
                                        <div className="Menu-vertical">
                                            <Icon size="medium" root="global" name="expand" className="icon-event" />
                                            {t('common:actions.Open')}/{t('common:translation.Edit')}
                                        </div>
                                    </VerticalMenu.Item>
                                    <RoleComponent roles={['SUPERADMIN', 'ADMIN']}>
                                        {e.rowData.Status === 'Active' && (
                                            <VerticalMenu.Item
                                                onClick={() => {
                                                    setOpenModalInactivateDelete(true);
                                                    setIdContract(e.rowData.Id);
                                                }}
                                            >
                                                <div className="Menu-vertical">
                                                    <Icon size="medium" root="global" name="badge-stop" className="icon-event" />
                                                    {t('bslbr:translation.Inactivate')}
                                                </div>
                                            </VerticalMenu.Item>
                                        )}
                                    </RoleComponent>
                                    <RoleComponent roles={['SUPERADMIN']}>
                                        {e.rowData.Status === 'Inactive' && (
                                            <VerticalMenu.Item
                                                onClick={() => {
                                                    setOpenModalActivate(true);
                                                    setIdContract(e.rowData.Id);
                                                }}
                                            >
                                                <div className="Menu-vertical">
                                                    <Icon
                                                        size="medium"
                                                        root="common"
                                                        name="document-sync"
                                                        className="icon-event"
                                                    />
                                                    {t('bslbr:translation.Activate')}
                                                </div>
                                            </VerticalMenu.Item>
                                        )}
                                    </RoleComponent>
                                </VerticalMenu>
                            </Popup>
                        )}
                        header={t('common:tabs.Options')}
                    />
                </DataTable>
                <DataTable.Pagination
                    totalItems={totalItems}
                    itemsPerPage={10}
                    onPageChange={pageNumber => {
                        const filterPage: FilterContractDTO = { ...filters, PageIndex: Number(pageNumber) };
                        getFilterContracts(filterPage);
                    }}
                />
            </div>
        );
    };

    const renderContent = () => {
        if (showAddContract) {
            return (
                <AddContractByForm
                    onSave={() => {
                        setShowAddContract(false);
                        getFilterContracts(filters);
                    }}
                    onBack={() => {
                        setShowAddContract(false);
                        getFilterContracts(filters);
                    }}
                    partner={allPartnerOptions}
                />
            );
        }
        if (showAddWarranty) {
            return (
                <AddWarrantyByForm
                    onSave={() => {
                        setShowAddWarranty(false);
                        getFilterContracts(filters);
                    }}
                    onBack={() => {
                        setShowAddWarranty(false);
                        getFilterContracts(filters);
                    }}
                    partner={allPartnerOptions}
                />
            );
        }
        if (showContractDetails) {
            return (
                <ContractDetails
                    onSave={() => {
                        setShowContractDetails(false);
                        getFilterContracts(filters);
                    }}
                    onBack={() => {
                        setShowContractDetails(false);
                        getFilterContracts(filters);
                    }}
                    contractDetails={contractDetails}
                    partner={allPartnerOptions}
                />
            );
        }
        if (showContractReport) {
            return (
                <ContractReport
                    onBack={() => {
                        setShowContractReport(false);
                        getFilterContracts(filters);
                    }}
                />
            );
        } else {
            return (
                <>
                    {searchFieldsRender()}
                    {renderModalInactivateContract()}
                    {renderModalActivateContract()}
                    {tableContractRender()}
                </>
            );
        }
    };

    return renderContent();
}

export default ContractSearch;
